import {Hash} from "~/interfaces/hashInterfaces"

const LEVELS = ['debug', 'info', 'warn', 'error'] as const
type TLevel = typeof LEVELS[number]

interface ILogger {
  state: Hash<boolean> // hash of processes to debug
  trace: Hash<boolean> // hash of trace configs for each process

  level: Hash<TLevel>

  start(process: string, level?: TLevel, trace?: boolean): void

  stop(process: string): void

  log(process: string, level: TLevel, ...messages: any[]): void

  log(process: string, ...messages: any[]): void

  logOnce(process: string, level: TLevel, ...messages: any[]): void

  logOnce(process: string, ...messages: any[]): void

  setLevel(process: string, level: TLevel)

  unsetLevel(process: string, level?: TLevel)

  error(process: string, ...messages: any[]): void

  errorOnce(process: string, ...messages: any[]): void

  warn(process: string, ...messages: any[]): void

  warnOnce(process: string, ...messages: any[]): void

  debug(process: string, ...messages: any[]): void

  debugOnce(process: string, ...messages: any[]): void

  puts(process: string, ...messages: any[]): void

  print(trace: boolean, process: string, ...messages: any[]): void

  table(process: string, table: any[] | Hash, filterColumns?: string[]): void

  tableOnce(process: string, level: TLevel, table: any[] | Hash, filterColumns?: string[]): void

  style: { [key in TLevel]: string }
}

const DEFAULT_LEVEL = 'info'
let Logger: ILogger

if (import.meta.env.MODE === 'development') {
  /**
   * @author Alex Henegar <alex@enovational.com>
   * Handles logging messages to console when debugging
   */
  Logger = {
    level: {},
    /**
     *
     * @param process - process to change the level for
     * @param level - used to determine message severity (and possibly when to log, not yet implemented)
     */
    setLevel(process: string, level: TLevel) {
      this.level[process] = level
    },
    unsetLevel(process: string, level: TLevel = DEFAULT_LEVEL) {
      this.level[process] = level
    },
    /**
     * Hash of processes as keys and boolean as values. Represents current state of app debugging
     */
    state: {},
    /**
     * Hash of processes as keys and boolean as values. Represents if "console.trace" should be used
     */
    trace: {},
    /**
     *
     * @param process - process to log for
     * @param messages - messages to be logged
     * first "message" can also be the log level used to determine style
     * @return Nothing
     */
    log(process: string, ...messages: [TLevel | any, ...any[]]): void {
      if (this.state[process]) {
        this.print(this.trace[process], process, ...processMessages(...messages))
      }
    },
    error(process: string, ...messages: any[]): void {
      if (this.state[process]) {
        console.error(
          `[${process}]`,
          ...processMessages(...messages),
        )
      }
    },
    warn(process: string, ...messages: any[]): void {
      this.log(process, 'warn', ...processMessages(...messages))
    },
    debug(process: string, ...messages: any[]): void {
      this.log(process, 'debug', ...processMessages(...messages))
    },
    table(process: string, table: any[] | Hash, filterColumns?: string[]): void {
      if (this.state[process]) {
        console.table(table, filterColumns)
      }
    },
    /**
     * @param process - process to log for
     * @param messages - messages to be logged
     * @return [undefined]
     */
    logOnce(process: string, ...messages: [TLevel | any, ...any[]]) {
      this.start(process)
      this.log(process, ...processMessages(...messages))
      this.stop(process)
    },
    errorOnce(process: string, ...messages: any[]) {
      this.start(process, 'error', true)
      this.error(process, ...processMessages(...messages))
      this.stop(process)
    },
    warnOnce(process: string, ...messages: any[]) {
      this.start(process, 'warn')
      this.warn(process, ...processMessages(...messages))
      this.stop(process)
    },
    debugOnce(process: string, ...messages: any[]) {
      this.start(process, 'debug', true)
      this.debug(process, ...processMessages(...messages))
      this.stop(process)
    },
    tableOnce(process: string, level: TLevel, table: any[] | Hash, filterColumns?: string[]) {
      this.start(process, level)
      this.table(process, table, filterColumns)
      this.stop(process)
    },
    puts(process: string, ...messages: any[]) {
      this.print(false, process, ...processMessages(...messages))
    },
    print(trace: boolean, process, ...messages: any[]) {
      let msgs = messages.slice()
      let style = this.level[this.process]
      if (LEVELS.includes(msgs[0])) style = msgs.shift()
      const printer = trace ? console.trace : console.log
      printer(
        `%c[${process}]`,
        this.style[style || DEFAULT_LEVEL],
        ...msgs,
      )
    },
    /**
     * @param process - process to start, logs will output when #log is run
     * @param level - start at the provided logging level
     * @param trace - use console.trace vs console.(log|warn|debug|error)
     * @return Nothing
     */
    start(process: string, level?: TLevel, trace?: boolean): void {
      this.state[process] = true
      this.trace[process] = trace
      if (level) this.setLevel(process, level)
    },
    /**
     * @param process - process to stop, logs will no longer output when #log is run
     * @return Nothing
     */
    stop(process: string): void {
      this.state[process] = false
      this.trace[process] = null
    },
    style: {
      'debug': 'color: blue; background-color: white; font-weight: bold;',
      'info': 'background-color: white; font-weight: bold;',
      'warn': 'color: yellow; background-color: black; font-weight: bold;',
      'error': 'color: red; background-color: white; font-weight: bold;',
    },
  }
} else {
  // Shell of a Logger to use in production so nothing is logged
  Logger = {
    print(trace: boolean, process: string, ...messages: any[]): void {
    },
    puts(process: string, ...messages: any[]): void {
    },
    level: undefined,
    state: {},
    trace: undefined,
    style: undefined,
    warn(process: string, ...messages: any[]): void {
    },
    warnOnce(process: string, ...messages: any[]): void {
    },
    debug(process: string, ...messages: any[]): void {
    },
    debugOnce(process: string, ...messages: any[]): void {
    },
    error(process: string, ...messages: any[]): void {
    },
    errorOnce(process: string, ...messages: any[]): void {
    },
    log(process: string, ...messages: any[]): void {
    },
    logOnce(process: string, ...messages: any[]): void {
    },
    setLevel(process: string, level: string) {
    },
    start(process: string, level?): void {
    },
    stop(process: string): void {
    },
    unsetLevel(process: string, level?: string) {
    },
    table(process: string, table: any[] | Hash, filterColumns?: string[]): void {
    },
    tableOnce(process: string, level: TLevel, table: any[] | Hash, filterColumns?: string[]): void {
    },
  }
}

export {Logger}

function processMessages(...messages) {
  return messages.map(m => typeof m === 'function' ? m() : m)
}